import { useEffect, useState } from "react";
import api from "../../api";
import { toast } from "react-toastify";
import "./ManageRoles.css";
import Layout from "../../components/Layout";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";
import PageHeader from "../../components/PageHeader/PageHeader";
import DataTable from "../../components/DataTable/DataTable";
import RowActions from "../../components/DataTable/RowActions";
import { useAuth } from "../../context/AuthContext";
import useCachedFetch from "../../hooks/useCachedFetch";
import useBreakpoint from "../../hooks/useBreakpoint";

const PAGE_SIZE = 15;

const roleConfig = {
  super_admin: { label: "Super Admin", className: "role-super" },
  admin: { label: "Admin", className: "role-admin" },
};

const statusConfig = {
  active: { label: "Active", className: "active" },
  blocked: { label: "Blocked", className: "status-blocked" },
  lockedOut: { label: "Locked Out", className: "status-blocked" },
};

const emptyForm = { email: "", username: "", password: "", role: "admin" };

const formatDate = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = timestamp._seconds
    ? new Date(timestamp._seconds * 1000)
    : new Date(timestamp);
  if (isNaN(date.getTime())) return "N/A";
  return `${String(date.getDate()).padStart(2, "0")} ${date.toLocaleString("en-GB", { month: "short" })}, ${date.getFullYear()}`;
};

const ManageRoles = () => {
  const { user: currentAdmin } = useAuth();
  const isSuperAdmin = currentAdmin?.role === "super_admin";
  const isMobile = useBreakpoint() === "mobile";

  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    data: admins,
    total,
    loading,
    fetch,
    refetch,
    invalidate,
  } = useCachedFetch({
    cacheKey: "admins",
    endpoint: "/admin-management/get-all",
    limit: PAGE_SIZE,
  });

  // Create form state
  const [form, setForm] = useState(emptyForm);
  const [formLoading, setFormLoading] = useState(false);

  // Confirm modal
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
    type: "delete",
  });

  const openConfirm = (title, message, onConfirm, type = "delete") => {
    setConfirmModal({ open: true, title, message, onConfirm, type });
  };

  const closeConfirm = () => {
    setConfirmModal({
      open: false,
      title: "",
      message: "",
      onConfirm: null,
      type: "delete",
    });
  };

  useEffect(() => {
    fetch(0); // first load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── CREATE ── */
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await api.post("/admin-management/create", form);
      toast.success("Admin created successfully!");
      setShowCreateModal(false);
      setForm(emptyForm);
      invalidate();
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to create admin");
    } finally {
      setFormLoading(false);
    }
  };

  /* ── UPDATE ROLE ── */
  const updateRole = async (id, role) => {
    try {
      await api.patch(`/admin-management/update-role/${id}`, { role });
      toast.success("Role updated!");
      invalidate();
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to update role");
    }
  };

  /* ── DELETE ── */
  const deleteAdmin = async (id) => {
    try {
      await api.delete(`/admin-management/delete/${id}`);
      toast.success("Admin deleted!");
      invalidate();
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to delete admin");
    }
  };

  /* ── RESET PASSWORD ── */
  const resetPassword = async (id, newPassword) => {
    try {
      await api.post(`/admin-management/reset-password/${id}`, { newPassword });
      toast.success("Password reset successfully!");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to reset password");
    }
  };

  /* ── ROW ACTIONS ── */
  const buildActions = (admin, isSelf) => {
    const inline = [
      {
        key: "copy",
        label: "Copy Email",
        icon: "content_copy",
        color: "neutral",
        onClick: () => {
          navigator.clipboard.writeText(admin.email);
          toast.success("Email copied!");
        },
      },
      {
        key: "delete",
        label: "Delete",
        icon: "delete",
        color: "error",
        hidden: !(isSuperAdmin && !isSelf), // super_admin only, never self
        onClick: () =>
          openConfirm(
            "Delete Admin",
            <span>
              Are you sure you want to delete <strong>{admin.username}</strong>?
              <br />
              This will remove their login access permanently.
            </span>,
            () => {
              deleteAdmin(admin.id);
              closeConfirm();
            },
            "permanent",
          ),
      },
    ];

    const menu = [
      {
        key: "reset",
        label: "Reset Password",
        icon: "lock_reset",
        hidden: !isSuperAdmin,
        onClick: () => {
          const newPass = prompt("Enter new password (min 6 chars):");
          if (newPass) resetPassword(admin.id, newPass);
        },
      },
    ];

    return { inline, menu };
  };

  /* ── TABLE COLUMNS ── */
  const columns = [
    {
      key: "admin",
      header: "Admin",
      width: "minmax(220px, 1fr)",
      skeleton: (
        <div className="user-cell">
          <div className="skeleton skeleton-avatar" />
          <div className="skeleton-text-group">
            <div className="skeleton skeleton-text" />
            <div className="skeleton skeleton-text short" />
          </div>
        </div>
      ),
      render: (admin) => {
        const isSelf = admin.id === currentAdmin?.uid;
        return (
          <div className="user-cell">
            <Avatar
              src={admin.imageUrl}
              sx={{
                width: 34,
                height: 34,
                bgcolor: "#E5E9E5",
                color: "#006663",
                flexShrink: 0,
              }}
            >
              {admin.username?.charAt(0)?.toUpperCase() || "A"}
            </Avatar>
            <div className="user-cell__text">
              <div className="name">
                {admin.username}
                {isSelf && <span className="you-badge">You</span>}
              </div>
              <div className="email">{admin.email}</div>
            </div>
          </div>
        );
      },
    },
    {
      key: "role",
      header: "Role",
      width: "minmax(130px, 0.4fr)",
      skeleton: <div className="skeleton skeleton-badge" />,
      render: (admin) => {
        const isSelf = admin.id === currentAdmin?.uid;
        const role = roleConfig[admin.role] || {
          label: admin.role,
          className: "role-admin",
        };

        if (isSuperAdmin && !isSelf) {
          return (
            <select
              className="role-select"
              aria-label={`Role for ${admin.username}`}
              value={admin.role}
              onChange={(e) => {
                const selectedRole = e.target.value; // capture immediately
                openConfirm(
                  "Change Role",
                  <span>
                    Change <strong>{admin.username}</strong>'s role to{" "}
                    <strong>{selectedRole}</strong>?
                  </span>,
                  () => {
                    updateRole(admin.id, selectedRole);
                    closeConfirm();
                  },
                  "save",
                );
              }}
            >
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
            </select>
          );
        }
        return (
          <span className={`role-badge ${role.className}`}>{role.label}</span>
        );
      },
    },
    {
      key: "status",
      header: "Status",
      width: "minmax(100px, 0.4fr)",
      skeleton: <div className="skeleton skeleton-badge" />,
      render: (admin) => {
        const status = statusConfig[admin.account_status] || {
          label: admin.account_status || "Unknown",
          className: "status-unknown",
        };
        return (
          <span className={`status ${status.className}`}>{status.label}</span>
        );
      },
    },
    {
      key: "joined",
      header: "Joined",
      width: "minmax(104px, 0.4fr)",
      skeleton: <div className="skeleton skeleton-text short" />,
      render: (admin) => (
        <span style={{ fontSize: "14px" }}>{formatDate(admin.createdAt)}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: isMobile ? "40px" : "minmax(260px, auto)",
      align: "end",
      sticky: isMobile,
      skeleton: isMobile ? (
        <div className="skeleton skeleton-btn" style={{ width: 28 }} />
      ) : (
        <div className="skeleton-actions">
          <div className="skeleton skeleton-btn" />
          <div className="skeleton skeleton-btn" />
        </div>
      ),
      render: (admin) => {
        const { inline, menu } = buildActions(
          admin,
          admin.id === currentAdmin?.uid,
        );
        return <RowActions inline={inline} menu={menu} collapsed={isMobile} />;
      },
    },
  ];

  /* ── UI ── */
  return (
    <Layout>
      <div className="roles-page">
        <PageHeader
          title="Manage Roles"
          description="Create and manage admin accounts and their permissions."
          actions={
            isSuperAdmin && (
              <Button
                variant="contained"
                color="primary"
                startIcon={
                  <span className="material-symbols-outlined">add</span>
                }
                onClick={() => setShowCreateModal(true)}
              >
                Add Admin
              </Button>
            )
          }
        />

        <DataTable
          columns={columns}
          rows={admins}
          loading={loading}
          skeletonRows={6}
          emptyMessage="No admins found"
          minWidth={isMobile ? 640 : 860}
          footer={
            <div className="dt-pagination">
              <span className="dt-pagination__summary">
                {total === 0
                  ? "No admins found"
                  : `${total} admin${total > 1 ? "s" : ""} total`}
              </span>
            </div>
          }
        />

        {/* CREATE ADMIN MODAL */}
        {showCreateModal && (
          <div className="modal-overlay">
            <div
              className="modal-box"
              role="dialog"
              aria-modal="true"
              aria-labelledby="add-admin-title"
            >
              <div className="modal-header">
                <h3 id="add-admin-title">Add New Admin</h3>
                <IconButton
                  size="small"
                  aria-label="Close"
                  onClick={() => setShowCreateModal(false)}
                >
                  <span className="material-symbols-outlined">close</span>
                </IconButton>
              </div>

              <form onSubmit={handleCreate} className="modal-form">
                <div className="form-group">
                  <label>Username</label>
                  <input
                    type="text"
                    placeholder="Enter username"
                    value={form.username}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, username: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    placeholder="Enter email"
                    value={form.email}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, email: e.target.value }))
                    }
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Password</label>
                  <input
                    type="password"
                    placeholder="Min 6 characters"
                    value={form.password}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, password: e.target.value }))
                    }
                    required
                    minLength={6}
                  />
                </div>

                <div className="form-group">
                  <label>Role</label>
                  <select
                    value={form.role}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, role: e.target.value }))
                    }
                  >
                    <option value="admin">Admin</option>
                    <option value="moderator">Moderator</option>
                  </select>
                </div>

                <div className="modal-footer">
                  <Button
                    variant="outlined"
                    color="neutral"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={formLoading}
                  >
                    {formLoading ? "Creating..." : "Create Admin"}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        type={confirmModal.type}
      />
    </Layout>
  );
};

export default ManageRoles;
